import React, { useState, useEffect } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import API from "../../api";
import toastr from "toastr";

interface PatientData {
  id?: number;
  P_code: string;
  P_name: string;
  prefix: string;
  gender: string;
  age: string;
  age_type: string;
  mobile: string;
  dob: string;
  blood_group: string;
  ob_number: string;
  room_number: string;
  id_card_type: string;
  id_number: string;
  email: string;
  address: string;
}

interface BloodGroup {
  id: string;
  blood_group: string;
}

interface PrefixOption {
  id: string | number;
  prefix_name: string;
}

interface PatientModalProps {
  show: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  patientData?: PatientData | null;
  onSuccess: () => void;
}

const ID_CARD_TYPES = [
  "Aadhaar Card",
  "Driving Licence",
  "Electoral Photo Identity Card",
  "Passport",
  "Permanent Account Number",
  "Ration Card",
];

const AGE_TYPES = ["Years", "Months", "Weeks", "Days"];
const GENDERS   = ["Male", "Female", "Trans Gender"];

// ── Calculate age from DOB ─────────────────────────────────────────────────────
const calcAgeFromDob = (
  dob: string
): { age: string; age_type: string } => {
  if (!dob) return { age: "", age_type: "Years" };

  const today = new Date();
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return { age: "", age_type: "Years" };

  const diffMs   = today.getTime() - birth.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 7)   return { age: String(diffDays),                           age_type: "Days"   };
  if (diffDays < 30)  return { age: String(Math.floor(diffDays / 7)),           age_type: "Weeks"  };
  if (diffDays < 365) return { age: String(Math.floor(diffDays / 30.4375)),     age_type: "Months" };

  const years  = today.getFullYear() - birth.getFullYear();
  const months = today.getMonth()    - birth.getMonth();
  const days   = today.getDate()     - birth.getDate();
  const exact  = months < 0 || (months === 0 && days < 0) ? years - 1 : years;

  return { age: String(exact), age_type: "Years" };
};

// ─────────────────────────────────────────────────────────────────────────────

const PatientModal: React.FC<PatientModalProps> = ({
  show,
  onClose,
  mode,
  patientData,
  onSuccess,
}) => {
  const defaultForm: PatientData = {
    P_code: "", P_name: "", prefix: "", gender: "",
    age: "", age_type: "Years", mobile: "", dob: "",
    blood_group: "", ob_number: "", room_number: "",
    id_card_type: "", id_number: "", email: "", address: "",
  };

  const [formData,        setFormData]        = useState<PatientData>(defaultForm);
  const [bloodGroups,     setBloodGroups]     = useState<BloodGroup[]>([]);
  const [prefixes,        setPrefixes]        = useState<PrefixOption[]>([]);
  const [loading,         setLoading]         = useState(false);
  const [prefixesLoaded,  setPrefixesLoaded]  = useState(false);
  const [errors,          setErrors]          = useState<Partial<Record<keyof PatientData, string>>>({});

  // ── API helpers ─────────────────────────────────────────────────────────────
  const fetchBloodGroups = async () => {
    try {
      const res = await API.get("/patients/getBloodGroups");
      setBloodGroups(res.data.data || []);
    } catch { toastr.error("Failed to load blood groups"); }
  };

  const fetchPrefixes = async () => {
    try {
      const res = await API.get("/patients/getPrefixes");
      setPrefixes(res.data.data || []);
      setPrefixesLoaded(true);
    } catch { toastr.error("Failed to load prefixes"); }
  };

  const fetchAutoCode = async () => {
    try {
      const res = await API.get("/patients/generateCode");
      setFormData((prev) => ({ ...prev, P_code: res.data.code || "" }));
    } catch { toastr.error("Failed to generate patient code"); }
  };

  useEffect(() => {
    fetchBloodGroups();
    fetchPrefixes();
  }, []);

  // ── Populate form when modal opens ──────────────────────────────────────────
  useEffect(() => {
    if (!show || !prefixesLoaded) return;

    document.body.style.overflow = "hidden";

    if (mode === "edit" && patientData) {
      setFormData({
        id:           patientData.id,
        P_code:       patientData.P_code       || "",
        P_name:       patientData.P_name       || "",
        prefix:       patientData.prefix       || "",
        gender:       patientData.gender       || "",
        age:          patientData.age          || "",
        age_type:     patientData.age_type     || "Years",
        mobile:       patientData.mobile       || "",
        dob:          patientData.dob ? patientData.dob.substring(0, 10) : "",
        blood_group:  patientData.blood_group  || "",
        ob_number:    patientData.ob_number    || "",
        room_number:  patientData.room_number  || "",
        id_card_type: patientData.id_card_type || "",
        id_number:    patientData.id_number    || "",
        email:        patientData.email        || "",
        address:      patientData.address      || "",
      });
    } else {
      setFormData(defaultForm);
      fetchAutoCode();
    }
    setErrors({});

    return () => { document.body.style.overflow = ""; };
  }, [show, mode, patientData, prefixesLoaded]);

  // ── Validation ───────────────────────────────────────────────────────────────
  // DOB, Mobile, and Address are optional. Mobile/Email are still format-checked
  // when a value is provided.
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof PatientData, string>> = {};

    if (!formData.prefix)                  newErrors.prefix   = "Prefix is required";
    if (!formData.P_name.trim())           newErrors.P_name   = "Patient name is required";
    if (!formData.gender)                  newErrors.gender   = "Gender is required";
    if (!formData.age.trim())              newErrors.age      = "Age is required";
    else if (isNaN(Number(formData.age)) || Number(formData.age) < 0)
                                           newErrors.age      = "Enter a valid age";
    if (!formData.age_type)                newErrors.age_type = "Age type is required";

    // Mobile is optional, but if entered must be a valid 10-digit number
    if (formData.mobile.trim() && !/^\d{10}$/.test(formData.mobile))
                                           newErrors.mobile   = "Mobile must be 10 digits";

    // Email is optional, but if entered must be a valid format
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
                                           newErrors.email    = "Invalid email format";

    // DOB and Address are optional — no validation required

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Input handler ─────────────────────────────────────────────────────────
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === "dob") {
      // Auto-calculate age whenever DOB changes
      const { age, age_type } = calcAgeFromDob(value);
      setFormData((prev) => ({ ...prev, dob: value, age, age_type }));
      setErrors((prev) => ({ ...prev, dob: undefined, age: undefined }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof PatientData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload = { ...formData };
      if (mode === "add") {
        await API.post("/patients/createPatient", payload);
        toastr.success("Patient added successfully!");
      } else if (mode === "edit" && formData.id) {
        await API.put(`/patients/updatePatient/${formData.id}`, payload);
        toastr.success("Patient updated successfully!");
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      toastr.error(error.response?.data?.message || "Failed to save patient");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      role="dialog"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        role="document"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ margin: "1.75rem auto", maxWidth: "900px" }}
      >
        <div className="modal-content">
          <Form onSubmit={handleSubmit}>
            {/* ── Header ─────────────────────────────────────────────────── */}
            <div className="modal-header">
              <h5 className="modal-title">
                {mode === "add" ? "Add New Patient" : "Edit Patient"}
              </h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
            </div>

            {/* ── Body ──────────────────────────────────────────────────── */}
            <div className="modal-body p-3">

              {/* ── Row 1 : Patient Code | OB Number | Room Number ──────── */}
              <Row className="mb-2">
                <Col md={4}>
                  <Form.Label className="mb-1 small fw-semibold">Patient Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="P_code"
                    value={formData.P_code || ""}
                    readOnly
                    placeholder="Auto-generated"
                    size="sm"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label className="mb-1 small fw-semibold">OB Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="ob_number"
                    placeholder="Enter OB number"
                    value={formData.ob_number}
                    onChange={handleInputChange}
                    size="sm"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label className="mb-1 small fw-semibold">Room Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="room_number"
                    placeholder="Enter room number"
                    value={formData.room_number}
                    onChange={handleInputChange}
                    size="sm"
                  />
                </Col>
              </Row>

              <hr className="my-2" />

              {/* ── Two-column section ──────────────────────────────────── */}
              <Row>
                {/* LEFT COLUMN */}
                <Col md={6} className="pe-md-2">

                  {/* Patient Name: prefix + name */}
                  <Form.Group className="mb-2">
                    <Form.Label className="mb-1 small fw-semibold">
                      Patient Name <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="d-flex gap-2 align-items-start">
                      <div style={{ flex: "0 0 30%" }}>
                        <select
                          className={`form-select form-select-sm ${errors.prefix ? "is-invalid" : ""}`}
                          name="prefix"
                          value={formData.prefix}
                          onChange={handleInputChange}
                        >
                          <option value="">Prefix</option>
                          {prefixes.map((p) => (
                            <option key={p.id} value={p.prefix_name}>
                              {p.prefix_name}
                            </option>
                          ))}
                        </select>
                        {errors.prefix && (
                          <div className="invalid-feedback d-block small">{errors.prefix}</div>
                        )}
                      </div>
                      <div style={{ flex: "0 0 calc(70% - 0.5rem)" }}>
                        <Form.Control
                          type="text"
                          name="P_name"
                          placeholder="Enter patient full name"
                          value={formData.P_name}
                          onChange={handleInputChange}
                          isInvalid={!!errors.P_name}
                          size="sm"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.P_name}
                        </Form.Control.Feedback>
                      </div>
                    </div>
                  </Form.Group>

                  {/* Gender & Date of Birth — SAME ROW */}
                  <Row className="mb-2">
                    <Col md={6}>
                      <Form.Label className="mb-1 small fw-semibold">
                        Gender <span className="text-danger">*</span>
                      </Form.Label>
                      <select
                        className={`form-select form-select-sm ${errors.gender ? "is-invalid" : ""}`}
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                      >
                        <option value="">Select gender</option>
                        {GENDERS.map((g) => (
                          <option key={g} value={g}>{g}</option>
                        ))}
                      </select>
                      {errors.gender && (
                        <div className="invalid-feedback d-block small">{errors.gender}</div>
                      )}
                    </Col>
                    <Col md={6}>
                      <Form.Label className="mb-1 small fw-semibold">
                        Date of Birth
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="dob"
                        value={formData.dob}
                        onChange={handleInputChange}
                        isInvalid={!!errors.dob}
                        max={new Date().toISOString().split("T")[0]}
                        size="sm"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.dob}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  {/* Age + Age Type */}
                  <Form.Group className="mb-2">
                    <Form.Label className="mb-1 small fw-semibold">
                      Age <span className="text-danger">*</span>
                      <small className="text-muted ms-1" style={{ fontWeight: 400, fontSize: "0.7rem" }}>
                        (auto-calculated from DOB)
                      </small>
                    </Form.Label>
                    <div className="d-flex gap-2">
                      <div style={{ flex: 2 }}>
                        <Form.Control
                          type="number"
                          name="age"
                          placeholder="Age"
                          value={formData.age}
                          onChange={handleInputChange}
                          isInvalid={!!errors.age}
                          min={0}
                          size="sm"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.age}
                        </Form.Control.Feedback>
                      </div>
                      <div style={{ flex: 1 }}>
                        <select
                          className="form-select form-select-sm"
                          name="age_type"
                          value={formData.age_type}
                          onChange={handleInputChange}
                        >
                          {AGE_TYPES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </Form.Group>

                  {/* Mobile */}
                  <Form.Group className="mb-2">
                    <Form.Label className="mb-1 small fw-semibold">
                      Mobile
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="mobile"
                      placeholder="Enter 10-digit mobile number"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      isInvalid={!!errors.mobile}
                      maxLength={10}
                      size="sm"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.mobile}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Blood Group */}
                  <Form.Group className="mb-2">
                    <Form.Label className="mb-1 small fw-semibold">Blood Group</Form.Label>
                    <select
                      className="form-select form-select-sm"
                      name="blood_group"
                      value={formData.blood_group}
                      onChange={handleInputChange}
                    >
                      <option value="">Select blood group</option>
                      {bloodGroups.map((bg) => (
                        <option key={bg.id} value={bg.blood_group}>{bg.blood_group}</option>
                      ))}
                    </select>
                  </Form.Group>
                </Col>

                {/* RIGHT COLUMN */}
                <Col md={6} className="ps-md-2">

                  {/* ID Card Type */}
                  <Form.Group className="mb-2">
                    <Form.Label className="mb-1 small fw-semibold">ID Proof Type</Form.Label>
                    <select
                      className="form-select form-select-sm"
                      name="id_card_type"
                      value={formData.id_card_type}
                      onChange={handleInputChange}
                    >
                      <option value="">Select ID Proof type</option>
                      {ID_CARD_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </Form.Group>

                  {/* ID Number */}
                  <Form.Group className="mb-2">
                    <Form.Label className="mb-1 small fw-semibold">ID Proof Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="id_number"
                      placeholder="Enter ID Proof number"
                      value={formData.id_number}
                      onChange={handleInputChange}
                      size="sm"
                    />
                  </Form.Group>

                  {/* Email */}
                  <Form.Group className="mb-2">
                    <Form.Label className="mb-1 small fw-semibold">Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={handleInputChange}
                      isInvalid={!!errors.email}
                      size="sm"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Address */}
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-1 small fw-semibold">
                      Address
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="address"
                      placeholder="Enter full address"
                      value={formData.address}
                      onChange={handleInputChange}
                      isInvalid={!!errors.address}
                      size="sm"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.address}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* ── Footer ────────────────────────────────────────────────── */}
            <div className="modal-footer py-2">
              <Button variant="secondary" onClick={onClose} disabled={loading} size="sm">
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading} size="sm">
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default PatientModal;